import type { FormField, InterviewSession } from '../types';
import { buildCampaignCsv, getExportDateBounds, type CampaignExportFilters, type CampaignExportResponse } from '../lib/campaign-csv';
import { matchesExportAnswers, validateExportFilters } from '../lib/campaign-export-filters';
import { getSupabase } from '../lib/supabase';

/** Export only answer data and visible metadata, excluding recordings and internal state. */
const EXPORT_COLUMNS = 'id, response_number, response_status, created_at, completed_at, interviewee_name, interviewee_email, interviewee_company, interviewee_job, calendar_bookings, form_answers, form_schema_snapshot, conversation_turns';

/** Load all matching responses through the signed-in client's row policies. */
export async function getCampaignExportResponses(sessionId: string, filters: CampaignExportFilters, signal?: AbortSignal, fields: FormField[] = [], conversationIds?: string[]): Promise<CampaignExportResponse[]> {
  filters = validateExportFilters(filters, fields);
  if (conversationIds?.length === 0) return [];
  const { from, until } = getExportDateBounds(filters);
  const cutoff = new Date().toISOString();
  const responses: CampaignExportResponse[] = [];
  let cursor: string | undefined;
  while (true) {
    signal?.throwIfAborted();
    let query = getSupabase().from('interview_data').select(EXPORT_COLUMNS)
      .eq('interview_session_id', sessionId)
      .lte('created_at', cutoff)
      .order('id', { ascending: true }).limit(500);
    if (conversationIds) query = query.in('id', conversationIds);
    if (cursor) query = query.gt('id', cursor);
    if (from) query = query.gte('created_at', from);
    if (until) query = query.lt('created_at', until);
    if (filters.status === 'in_progress') query = query.or('response_status.eq.in_progress,response_status.is.null');
    else if (filters.status) query = query.eq('response_status', filters.status);
    if (signal) query = query.abortSignal(signal);
    const { data, error } = await query;
    signal?.throwIfAborted();
    if (error) throw new Error(`Failed to export campaign responses: ${error.message}`);
    if (!data?.length) break;
    responses.push(...(data as unknown as CampaignExportResponse[]).filter(row => matchesExportAnswers(row, fields, filters)));
    cursor = data[data.length - 1].id;
    // Continue until an empty page, even if the server caps rows below our requested limit.
  }
  return responses;
}

/** Download a complete CSV, returning zero when no responses match. */
export async function downloadCampaignCsv(session: InterviewSession, filters: CampaignExportFilters, signal?: AbortSignal, conversationIds?: string[]): Promise<number> {
  const responses = await getCampaignExportResponses(session.id, filters, signal, session.form_schema ?? [], conversationIds);
  signal?.throwIfAborted();
  if (!responses.length) return 0;
  const csv = buildCampaignCsv(session.form_schema ?? [], responses);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const name = session.target_name.replace(/[^\p{L}\p{N}_-]+/gu, '-').replace(/^-+|-+$/g, '').slice(0, 100) || 'campaign';
  link.href = url;
  link.download = `${name}-${new Date().toISOString().slice(0, 10)}.csv`;
  try {
    document.body.appendChild(link);
    link.click();
  } finally {
    link.remove();
    // Give the browser time to consume the URL before releasing it.
    window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
  }
  return responses.length;
}
